import React, { Fragment, useEffect, useMemo, useState } from "react";

import Decimal from "decimal.js";
import { useParams } from "react-router-dom";

import useGetHousehold from "@fartherfinance/frontend/api/Households/hooks/useGetHousehold";
import useGetHouseholdPerformance from "@fartherfinance/frontend/api/Households/hooks/useGetHouseholdPerformance";
import { HouseholdId } from "@fartherfinance/frontend/api/Households/schemas";
import { TimeFrame } from "@fartherfinance/frontend/api/PerformanceGroups/hooks/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import GridRow from "@src/sharedComponents/GridRow/GridRow";
import toUSD from "@src/utils/toUSD";

import FartherPerformanceCard from "./HouseholdAccountsCards/FartherPerformanceCard";
import HouseholdAUMCard from "./HouseholdAccountsCards/HouseholdAUMCard";
import HouseholdAccountsTables from "./HouseholdAccountsTables/HouseholdAccountsTables";

import styles from "./HouseholdAccounts.module.css";

const timeFrameOptions: TimeFrame[] = ["30D", "3M", "6M", "1Y", "YTD"] as const;

const HouseholdAccounts = (): JSX.Element => {
  const { householdId } = useParams<{ householdId: HouseholdId }>();

  const auth = useAdvisorRequestAuth();

  const [timeFrame, setTimeFrame] = useState<TimeFrame>("1Y");

  useEffect(() => {
    trackEvent({
      name: "Advisor Open Household Accounts Tab",
    });
  }, []);

  const handleOnSetTimeRange = (timeRange: TimeFrame): void => {
    setTimeFrame(timeRange);
  };

  const { data: householdResponseData } = useGetHousehold(householdId, auth);

  const {
    data: householdPerformanceResponseData,
    isLoading: isLoadingPerformance,
  } = useGetHouseholdPerformance(householdId, auth);

  const householdAUM = useMemo(() => {
    if (householdResponseData === undefined) {
      return 0;
    }
    const totalAssets = new Decimal(householdResponseData?.data.totalAssets);

    return totalAssets.toNumber();
  }, [householdResponseData]);

  const householdPerformance = useMemo((): string => {
    const performance =
      householdPerformanceResponseData?.data.performance || [];

    const currentPerformanceData = performance.find(
      (perf) => perf.period === timeFrame
    );
    return currentPerformanceData?.performance || "0.0%";
  }, [householdPerformanceResponseData, timeFrame]);

  return (
    <Fragment>
      <GridRow className={styles.grid} maxColumns={3}>
        <HouseholdAUMCard
          isLoading={isLoadingPerformance}
          amount={toUSD(householdAUM)}
        />
        <FartherPerformanceCard
          performance={householdPerformance}
          timeFrame={timeFrame}
          isLoading={isLoadingPerformance}
          timeFrameOptions={timeFrameOptions}
          onSetTimeRange={handleOnSetTimeRange}
        />
      </GridRow>
      <HouseholdAccountsTables />
    </Fragment>
  );
};

export default HouseholdAccounts;
